import { Box, Button, Modal, Typography } from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

type props = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  handleDelete: () => void;
  name: string;
  /** Override the default "Delete {name}?" title (e.g. for a non-destructive confirm) */
  title?: string;
  /** Override the default "This action cannot be undone…" body copy */
  description?: string;
  /** Override the confirm button label (default "Delete") */
  confirmLabel?: string;
  /** Confirm button color theme — "danger" (default, red) or "primary" (blue) */
  confirmColor?: "danger" | "primary";
};

const WarnModal = ({
  open,
  setOpen,
  handleDelete,
  name,
  title,
  description,
  confirmLabel,
  confirmColor = "danger",
}: props) => {
  const isPrimary = confirmColor === "primary";
  const accentBg = isPrimary
    ? "#dbeafe"
    : "#fef3c7";
  const accentColor = isPrimary
    ? "#2563eb"
    : "#d97706";
  const confirmGradient = isPrimary
    ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
    : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)";
  const confirmGradientHover = isPrimary
    ? "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)"
    : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)";
  const confirmShadow = isPrimary
    ? "0 4px 14px 0 rgba(37, 99, 235, 0.25)"
    : "0 4px 14px 0 rgba(239, 68, 68, 0.25)";
  const confirmShadowHover = isPrimary
    ? "0 6px 20px rgba(37, 99, 235, 0.35)"
    : "0 6px 20px rgba(239, 68, 68, 0.35)";
  const style = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    padding: "32px",
    outline: "none",
    border: "1px solid #f1f5f9",
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(15, 23, 42, 0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: accentBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "20px",
            }}
          >
            <WarningAmberRoundedIcon
              sx={{
                fontSize: 32,
                color: accentColor,
              }}
            />
          </Box>

          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            sx={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#1e293b",
              marginBottom: "8px",
              letterSpacing: "-0.02em",
            }}
          >
            {title ?? `Delete ${name}?`}
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              color: "#64748b",
              lineHeight: 1.5,
            }}
          >
            {description ?? "This action cannot be undone. Are you sure you want to proceed?"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            marginTop: "24px",
          }}
        >
          <Button
            onClick={() => setOpen(false)}
            sx={{ 
              color: "#64748b", 
              borderColor: "#e2e8f0",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              padding: "10px 24px",
              "&:hover": {
                borderColor: "#cbd5e1",
                backgroundColor: "#f8fafc",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              setOpen(false);
              handleDelete();
            }}
            sx={{
              background: confirmGradient,
              color: "#ffffff",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              padding: "10px 24px",
              boxShadow: confirmShadow,
              "&:hover": {
                background: confirmGradientHover,
                boxShadow: confirmShadowHover,
              },
            }}
          >
            {confirmLabel ?? "Delete"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default WarnModal;
